import type { ICmsComplexTextField, ICmsLink, TitleBlock } from "@mw/shared/payload-types";

import { formatCTA } from "@utilities/payload";

import { Block } from "../Block";
import { RichText } from "@ui/components/core/RichText";
import { clsx } from "clsx";
import { ContextualNavigationTracker } from "@ui/components/navigation/ContextualNavigation";
import { EventName } from "@mw/shared/analyticsEventNames";
import { pxToRem } from "@ui/lib/utils";
import { CTAButton } from "@ui/components/core/CTAButton";

interface BlockTitleProps extends Omit<TitleBlock, "blockType" | "id" | "blockName" | "primaryLink" | "secondaryLink"> {
  className?: string;
  topPadding?: "default" | "none";
  primaryLink?: TitleBlock["primaryLink"] | ICmsLink | null | undefined;
  secondaryLink?: TitleBlock["secondaryLink"] | ICmsLink | null | undefined;
}

export function BlockTitle(props: BlockTitleProps) {
  const isHero = props.layout === "default-hero" || props.layout === "two-column-hero";

  // Text content
  const titleComplex = !!props.title
    ? ({
        text: props.title,
      } as ICmsComplexTextField)
    : props.titleComplex;
  const title = titleComplex?.text
    ? { text: titleComplex.text, size: titleComplex.options?.size, tag: titleComplex.options?.tag }
    : undefined;
  if (!title) {
    return null;
  }
  const titleWidth = props.titleWidth;
  const titleSize = title?.size || "medium";
  const capHeight = titleSize === "large" ? 34.5 : titleSize === "base" ? 11.5 : 26;

  const preTitleComplex = !!props.preTitle
    ? ({
        text: props.preTitle,
      } as ICmsComplexTextField)
    : props.preTitleComplex;
  const preTitle = preTitleComplex?.text
    ? { text: preTitleComplex.text, tag: preTitleComplex.options?.tag }
    : undefined;

  const subtitleComplex = !!props.subtitle
    ? ({
        text: props.subtitle,
      } as ICmsComplexTextField)
    : props.subtitleComplex;
  const description = subtitleComplex?.text
    ? { text: subtitleComplex.text, tag: subtitleComplex.options?.tag }
    : undefined;

  const linkText = props.linkText?.textWrapper?.text_html
    ? {
        text: <RichText content={props.linkText.textWrapper.text_html} size='2xs' color='gray' />,
        style: props.linkText.style,
      }
    : undefined;

  // Buttons
  const primaryCTA = formatCTA(props.primaryLink);
  const primaryLinkEventName = isHero ? "mw_hero_primary_link" : props.primaryLinkEventName;

  const secondaryCTA = formatCTA(props.secondaryLink);
  const secondaryLinkEventName = isHero ? "mw_hero_secondary_link" : props.secondaryLinkEventName;

  const hasButtons = !!primaryCTA || !!secondaryCTA;

  let buttonPosition = props.buttonPosition ?? "auto";
  if (buttonPosition === "auto") {
    if (description) {
      buttonPosition = "left";
    } else {
      buttonPosition = "right";
    }
  }

  // Temporary fix to prevent breaking certain page titles during the transition period.
  if (props.layout === "two-column-hero" && buttonPosition === "right" && !description) {
    buttonPosition = "left";
  }

  // Layout
  let layout: "default" | "centered" | "two-column";
  const layoutProp = props.layout ?? "default";
  if (layoutProp === "default" || layoutProp === "default-hero") {
    layout = props.titleAlignment === "center" ? "centered" : "default";
  } else {
    if (!description && (!hasButtons || buttonPosition === "left")) {
      layout = "default";
    } else {
      layout = "two-column";
    }
  }

  const buttons = hasButtons ? (
    <div className={clsx("tw-flex tw-flex-wrap tw-gap-2", layout === "centered" && "tw-justify-center")}>
      {primaryCTA && (
        <CTAButton
          {...primaryCTA}
          buttonVariants={{ size: "lg" }}
          {...{
            eventName: primaryLinkEventName ? EventName[primaryLinkEventName] : undefined,
            eventProperties: props.primaryLink?.label?.trim() ? { label: props.primaryLink.label.trim() } : undefined,
          }}
        />
      )}
      {secondaryCTA && (
        <CTAButton
          {...secondaryCTA}
          buttonVariants={{ size: "lg", variant: "elevated" }}
          {...{
            eventName: secondaryLinkEventName ? EventName[secondaryLinkEventName] : undefined,
            eventProperties: props.secondaryLink?.label?.trim()
              ? { label: props.secondaryLink.label.trim() }
              : undefined,
          }}
        />
      )}
    </div>
  ) : null;

  const linkTextElement = linkText && (
    <div
      className={clsx(
        "tw-mt-3",
        linkText.style === "italic" && "tw-italic",
        layout === "centered" && "tw-text-center",
        layout === "centered" ? "tw-px-2" : "tw-pl-2",
      )}
    >
      {linkText.text}
    </div>
  );

  const Title = title?.tag || "h2";
  const PreTitle = preTitle?.tag || "p";
  const Description = description?.tag || "p";

  return (
    <Block
      name='title'
      className={clsx(props.className, isHero && props.topPadding !== "none" && "tw-pt-16 md:tw-pt-20 lg:tw-pt-25")}
    >
      {preTitle && (
        <PreTitle
          className={clsx(
            "tw-block tw-type-sm tw-text-gray-500 tw-font-medium tw-mb-7",
            layout === "centered" && "tw-text-center",
          )}
        >
          {preTitle.text}
        </PreTitle>
      )}
      <div
        className={clsx(layout === "two-column" && "tw-flex tw-flex-col lg:tw-grid lg:tw-grid-cols-12 lg:tw-gap-x-12")}
      >
        <div
          className={clsx(
            "tw-contents lg:tw-block",
            layout === "two-column" && [titleWidth === "wide" ? "lg:tw-col-span-7" : "lg:tw-col-span-6"],
          )}
        >
          {title && (
            <Title
              className={clsx(
                "tw-block tw-text-black tw-text-balance",
                titleSize === "base" && "tw-type-base",
                titleSize === "large" && "tw-type-6xl",
                titleSize === "medium" && "tw-type-5xl",
                layout === "default" && "tw-max-w-3xl",
                layout === "two-column" && "tw-max-w-lg lg:tw-max-w-none",
                layout === "centered" && "tw-max-w-2xl tw-text-center tw-mx-auto",
              )}
            >
              {title.text === "Bringing technology to life" ? (
                <>
                  {/* Temporary fix for the home page to force a line break after "Bringing" */}
                  {"Bringing"}
                  <span className='tw-table'>{" technology to life"}</span>
                </>
              ) : (
                title.text
              )}
            </Title>
          )}
          {hasButtons && buttonPosition === "left" && layout === "two-column" && (
            <div className='tw-mt-8 tw-order-last lg:tw-order-none'>
              {buttons}
              {linkTextElement}
            </div>
          )}
        </div>
        {description && (
          <div
            className={clsx(
              "tw-flex tw-flex-col",
              layout === "two-column" && [
                "tw-mt-8 lg:tw-mt-0 tw-max-w-2xl lg:tw-max-w-none",
                titleWidth === "wide" ? "lg:tw-col-span-5" : "lg:tw-col-span-6",
              ],
              (layout === "default" || layout === "centered") && "tw-mt-8 tw-max-w-2xl",
              layout === "centered" && "tw-text-center tw-mx-auto",
            )}
          >
            <Description
              className={clsx(
                "tw-block tw-type-base tw-mt-auto",
                layout === "centered" && "tw-text-balance",
                layout === "default" && "tw-text-balance",
                layout === "two-column" && "tw-text-balance lg:tw-text-pretty",
              )}
            >
              {description.text}
            </Description>
            {/* Spacer equal to the height of the button (48px) plus its top margin (32px) */}
            {hasButtons && buttonPosition === "left" && layout === "two-column" && (
              <div
                className='tw-h-0 tw-flex-auto'
                style={{ maxHeight: pxToRem(48 + 32 + (linkTextElement ? 30 : 0)) }}
              />
            )}
            {hasButtons && buttonPosition === "right" && layout === "two-column" && (
              <div className='tw-mt-8'>
                {buttons}
                {linkTextElement}
              </div>
            )}
          </div>
        )}
        {!description && hasButtons && buttonPosition === "right" && layout === "two-column" && (
          <div
            className={clsx(
              "tw-relative tw-mt-8 lg:tw-mt-0",
              layout === "two-column" && [titleWidth === "wide" ? "lg:tw-col-span-5" : "lg:tw-col-span-6"],
            )}
          >
            {/* On desktop the button is positioned so that it's centered-aligned with the title when
              there's only one line, and aligned to the baseline when there's more than one line.
              Additionally, the button is absolutely positioned so it doesn't affect the height of the block.
              However, when linkTextElement exists, we use normal flow to avoid overflow issues. */}
            <div
              className={clsx(
                linkTextElement ? "lg:tw-flex lg:tw-flex-col lg:tw-items-end" : "lg:tw-absolute lg:tw-right-0",
              )}
              style={
                linkTextElement
                  ? undefined
                  : // max((buttonHeight - titleCapHeight) / -2, 100% - buttonHeight)
                    { top: `max(${pxToRem((48 - capHeight) / -2)}, calc(100% - ${pxToRem(48)}))` }
              }
            >
              {buttons}
              {linkTextElement}
            </div>
          </div>
        )}
        {hasButtons && (layout === "default" || layout === "centered") && <div className='tw-mt-8'>{buttons}</div>}
        {linkTextElement && !hasButtons && <div className='tw-mt-8 tw-col-span-full'>{linkTextElement}</div>}
        {linkTextElement && layout !== "two-column" && hasButtons && linkTextElement}
      </div>
      {isHero && <ContextualNavigationTracker />}
    </Block>
  );
}
